/**
 * @Description 动态保护距离模块
 */

import { FC, useEffect, useRef, useState } from 'react';
import styles from './MXBCModule.module.less';
import V2Title from '@/common/components/Feedback/V2Title';
import V2AMap from '@/common/components/Map/V2AMap';
import V2Table from '@/common/components/Data/V2Table';
import { useMethods } from '@lhb/hook';
import { showMarker } from '@/common/utils/map';

/** 圆环配置：半径越小层级越高，分流值越大 */
const CIRCLE_CONFIG = [
  { label: '极高', range: '≥20%', radius: 50, color: '#FF46A1', zIndex: 14, cups: '3.5%', revenue: '3%', flowCups: '17000杯/月', flowRevenue: '1.6万/月' },
  { label: '高', range: '10%~20%', radius: 100, color: '#C246FF', zIndex: 13, cups: '2.5%', revenue: '2%', flowCups: '12000杯/月', flowRevenue: '1.1万/月' },
  { label: '中', range: '5%~10%', radius: 150, color: '#46AAFF', zIndex: 12, cups: '1.5%', revenue: '1.2%', flowCups: '8000杯/月', flowRevenue: '0.7万/月' },
  { label: '低', range: '≤5%', radius: 200, color: '#33E8D0', zIndex: 11, cups: '0.8%', revenue: '0.5%', flowCups: '4000杯/月', flowRevenue: '0.3万/月' },
];

/** Tab选项 */
const TAB_OPTIONS = [
  { key: 'all', label: '外卖+堂食' },
  { key: 'delivery', label: '外卖' },
  { key: 'dineIn', label: '堂食' },
];

interface MXBCModuleProps {
  detail: any;
};
const MXBCModule: FC<MXBCModuleProps> = ({
  detail
}) => {
  const [mapIns, setMapIns] = useState<any>(null); // 地图实例
  const [activeTab, setActiveTab] = useState('all'); // 当前选中Tab
  const circlesRef = useRef<any[]>([]); // Circle覆盖物实例列表
  const contentMarker: any = useRef(); // hover卡片Marker，全局复用
  const isHoveringRef = useRef<any>({ circle: false, card: false }); // hover状态追踪

  // 地图加载完成后初始化hover卡片Marker并绘制圆环
  useEffect(() => {
    if (!mapIns) return;
    // 创建全局复用的hover卡片Marker
    const labelMarker = new window.AMap.Marker({
      content: ' ',
      map: mapIns,
      zIndex: 100,
    });
    contentMarker.current = labelMarker;
    // 卡片Marker的hover事件，防止鼠标移到卡片上时卡片消失
    labelMarker.on('mouseover', () => {
      isHoveringRef.current.card = true;
    });
    labelMarker.on('mouseout', () => {
      isHoveringRef.current.card = false;
      setTimeout(() => {
        if (Object.values(isHoveringRef.current).every((item) => !item)) {
          methods.removeLabelMarker();
        }
      }, 200);
    });
    // 绘制圆环
    methods.drawCircles();
    return () => {
      methods.clearCircles();
    };
  }, [mapIns]);

  const methods = useMethods({
    /** 地图加载完成回调 */
    mapLoadedHandle(ins: any) {
      setMapIns(ins);
      // 设置地图中心点和缩放级别
      if (detail?.lng && detail?.lat) {
        ins.setCenter([+detail.lng, +detail.lat]);
        ins.setZoom(17);
      }
    },

    /** 绘制四个同心圆覆盖物 */
    drawCircles() {
      if (!mapIns || !detail?.lng || !detail?.lat) return;
      const center = new window.AMap.LngLat(+detail.lng, +detail.lat);
      CIRCLE_CONFIG.forEach((config) => {
        const circle = new window.AMap.Circle({
          center,
          radius: config.radius,
          fillColor: config.color,
          fillOpacity: 0.08,
          strokeColor: config.color,
          strokeWeight: 1.5,
          strokeOpacity: 0.6,
          strokeStyle: 'dashed', // 虚线边框
          zIndex: config.zIndex,
          bubble: false, // 不冒泡，确保hover能捕获到最上层的Circle
        });
        mapIns.add(circle);
        // 绑定hover事件
        circle.on('mouseover', (e: any) => {
          isHoveringRef.current.circle = true;
          methods.createLabelMarker(config, e);
        });
        circle.on('mouseout', () => {
          isHoveringRef.current.circle = false;
          setTimeout(() => {
            if (Object.values(isHoveringRef.current).every((item) => !item)) {
              methods.removeLabelMarker();
            }
          }, 200);
        });
        circlesRef.current.push(circle);
      });
    },

    /** 清除所有圆环覆盖物 */
    clearCircles() {
      circlesRef.current.forEach((circle) => {
        circle?.setMap?.(null);
      });
      circlesRef.current = [];
    },

    /** Tab切换：销毁圆环后延迟500ms重新绘制 */
    handleTabChange(key: string) {
      if (key === activeTab) return;
      setActiveTab(key);
      methods.clearCircles();
      methods.removeLabelMarker();
      setTimeout(() => {
        methods.drawCircles();
      }, 500);
    },

    /** hover卡片的表格列定义 */
    getHoverColumns() {
      // 红色向下箭头渲染
      const renderWithArrow = (value: string) => (
        <span>
          {value}
          <span style={{ color: '#FF4D4F', marginLeft: 2, fontSize: 12 }}>↓</span>
        </span>
      );
      return [
        { key: 'type', title: '类型', width: 80 },
        { key: 'cups', title: '杯量', width: 100, render: (value: string) => renderWithArrow(value) },
        { key: 'revenue', title: '营业额', width: 100, render: (value: string) => renderWithArrow(value) },
      ];
    },

    /** 创建/更新hover卡片内容和位置 */
    createLabelMarker(config: typeof CIRCLE_CONFIG[0], e: any) {
      // 获取鼠标在地图上的经纬度位置
      const lnglat = e?.lnglat ? [e.lnglat.getLng(), e.lnglat.getLat()] : [+detail.lng, +detail.lat];
      contentMarker.current.setAnchor('bottom-center');
      contentMarker.current.setOffset(new window.AMap.Pixel(0, -8));
      contentMarker.current.setExtData({ id: config.radius });
      setTimeout(() => {
        contentMarker.current.setPosition(lnglat);
      }, 0);
      // hover卡片的表格数据
      const tableData = [
        { id: 1, type: '分流百分比', cups: config.cups, revenue: config.revenue },
        { id: 2, type: '分流数量', cups: config.flowCups, revenue: config.flowRevenue },
      ];
      // 渲染hover卡片内容
      showMarker(contentMarker.current, (
        <div className={styles.hoverCard}>
          <div className={styles.hoverCardTitle}>新店距老店{detail?.radius || config.radius}m</div>
          <div className={styles.hoverCardTableWrap}>
            <V2Table
              rowKey='id'
              defaultColumns={methods.getHoverColumns()}
              hideColumnPlaceholder
              pagination={false}
              onFetch={() => Promise.resolve({ dataSource: tableData, count: tableData.length })}
              scroll={{ x: 'max-content' }}
            />
          </div>
        </div>
      ));
    },

    /** 移除hover卡片内容 */
    removeLabelMarker() {
      if (mapIns && contentMarker.current) {
        contentMarker.current.setExtData({});
        contentMarker.current.setContent(' ');
      }
    },
  });

  return (
    <>
      <V2Title
        divider
        type='H2'
        text='动态保护距离'
        className='mt-12'
      />
      <div className={styles.mapContainer}>
        <V2AMap
          mapOpts={{
            showIndoorMap: true,
          }}
          loaded={methods.mapLoadedHandle}
        />
        {/* 自定义Tab切换 - 左上角 */}
        <div className={styles.tabBar}>
          {TAB_OPTIONS.map((tab) => (
            <div
              key={tab.key}
              className={`${styles.tabItem} ${activeTab === tab.key ? styles.tabItemActive : ''}`}
              onClick={() => methods.handleTabChange(tab.key)}
            >
              {tab.label}
            </div>
          ))}
        </div>
        {/* 图例说明 - 左下角 */}
        <div className={styles.legend}>
          <div className={styles.legendTitle}>影响程度</div>
          {CIRCLE_CONFIG.map((item) => (
            <div
              key={item.label}
              className={styles.legendItem}
            >
              <span
                className={styles.legendDot}
                style={{ borderColor: item.color }}
              />
              {item.label}{item.range}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default MXBCModule;
